import { useEffect, useMemo, useRef, useState } from "react"
import { Dialog, DialogContent, DialogTitle } from "@/clientWeb/components/ui/vcDialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Megaphone, Send, Check } from "lucide-react"
import type { BreakoutRoom } from "@vc/viewmodels/breakout/useBreakoutRooms"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  useBreakoutBroadcastStore,
  MAIN_ROOM_TARGET_ID,
  MAIN_ROOM_NAME,
} from "@/features/meeting/store/breakout-broadcast.store"
import { BreakoutBroadcastThread } from "./BreakoutBroadcastThread"

interface BroadcastMessageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rooms: BreakoutRoom[]
  /** `roomIds` empty = every room. `id` lets the sender's own echo be
   *  recognised when it comes back through the room they are standing in. */
  onSend: (message: string, roomIds: string[], id: string) => void
  /** Shown as the sender on the host's own messages. */
  senderName?: string
}

/** Kept from the previous screen — the four things a host actually sends. */
const QUICK_MESSAGES = [
  "5 minutes remaining",
  "Please return soon",
  "Great work everyone!",
  "Need any help?",
]

/**
 * The host's broadcast screen.
 *
 * Reads as a conversation rather than a form. The previous version was a
 * compose box with a count of rooms beside it: you could send, and then had no
 * record of what you had already sent — so mid-meeting the host could not tell
 * whether the "5 minutes left" warning had gone out, and the only fix was to
 * send it again.
 *
 * Rooms are a CHOICE now, not a fixed audience. A broadcast used to go
 * everywhere, which is wrong the moment one room is running late and the rest
 * are not.
 *
 * Four rows: quick messages, the conversation, the composer, then the room
 * picker and the close action side by side at the foot.
 */
export function BroadcastMessageDialog({
  open,
  onOpenChange,
  rooms,
  onSend,
  senderName = "Host",
}: BroadcastMessageDialogProps) {
  const t = useTranslation()
  const [message, setMessage] = useState("")
  const messages = useBreakoutBroadcastStore((s) => s.messages)
  const selectedRoomIds = useBreakoutBroadcastStore((s) => s.selectedRoomIds)
  const toggleRoom = useBreakoutBroadcastStore((s) => s.toggleRoom)
  const selectAllRooms = useBreakoutBroadcastStore((s) => s.selectAllRooms)
  const pushMessage = useBreakoutBroadcastStore((s) => s.send)
  const myPeerId = useBreakoutBroadcastStore((s) => s.myPeerId)
  const markRead = useBreakoutBroadcastStore((s) => s.markRead)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  // Everywhere a message can go: the main room first, then the breakouts.
  // The main room is in the list rather than implied because everyone who has
  // not walked into a breakout is standing in it — leaving it out meant a
  // targeted message could not reach them at all, while putting it in
  // unconditionally meant "send to Room 2" reached the whole meeting.
  const audience = useMemo(
    () => [
      { id: MAIN_ROOM_TARGET_ID, name: MAIN_ROOM_NAME, count: null as number | null },
      ...rooms.map((r) => ({ id: r.id, name: r.name, count: r.participants?.length ?? 0 })),
    ],
    [rooms],
  )

  // Empty selection means everywhere — see the store for why that is not the
  // same as listing every id.
  const allSelected = selectedRoomIds.length === 0
  const targets = useMemo(
    () => (allSelected ? audience : audience.filter((a) => selectedRoomIds.includes(a.id))),
    [allSelected, audience, selectedRoomIds],
  )
  const recipientCount = targets.reduce((n, a) => n + (a.count ?? 0), 0)
  const canSend = message.trim().length > 0 && targets.length > 0

  useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
    // Opening the thread IS reading it — a co-host's message should not keep
    // its badge while the host is looking straight at it.
    markRead()
    // Back to every room each time this is opened. The selection is sticky
    // while the screen stays open, which is what you want when sending a few
    // messages to one room — but carrying it into the NEXT sitting is how a
    // "5 minutes left" ends up going to one room out of four because somebody
    // narrowed it an hour earlier. Narrowing is the case that should have to
    // be chosen.
    selectAllRooms()
  }, [open, markRead, selectAllRooms])

  // Anything arriving while the thread is already open is read too.
  useEffect(() => {
    if (open) markRead()
  }, [open, messages.length, markRead])

  const handleSend = () => {
    const text = message.trim()
    if (!text || targets.length === 0) return
    const roomIds = allSelected ? [] : targets.map((a) => a.id)
    const id = pushMessage({
      text,
      senderName,
      senderId: myPeerId,
      roomIds,
      roomNames: targets.map((a) => a.name),
    })
    onSend(text, roomIds, id)
    setMessage("")
    inputRef.current?.focus()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidthClass="max-w-[640px]" className="p-0 gap-0 overflow-hidden">
        {/* Header */}
        {/* No close button of our own: DialogContent renders one in the
            corner already, and a second X beside it read as two different
            ways out of the same dialog. */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 pr-12 dark:border-border">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/15">
              <Megaphone className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                {t("controlBar.broadcastMessage")}
              </DialogTitle>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                {allSelected
                  ? `${t("controlBar.allRooms")} · ${audience.length} ${t("controlBar.rooms")}`
                  : `${targets.length}/${audience.length} ${t("controlBar.rooms")}`}
              </p>
            </div>
          </div>
        </div>

        <div className="flex h-[500px] min-h-0 flex-col">
          {/* Row 1 — quick messages */}
          <div className="flex flex-wrap gap-2 border-b border-slate-200 px-4 py-3 dark:border-border">
            {QUICK_MESSAGES.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setMessage(q)
                  inputRef.current?.focus()
                }}
                className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-amber-300 hover:bg-amber-100 hover:text-amber-700 dark:border-border dark:bg-muted dark:text-foreground dark:hover:bg-amber-900/30 dark:hover:text-amber-400"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Row 2 — the conversation */}
          <BreakoutBroadcastThread
            messages={messages}
            emptyHint={t("controlBar.typeBroadcastMessage")}
            myPeerId={myPeerId}
          />

          {/* Row 3 — composer */}
          <div className="border-t border-slate-200 px-4 py-3 dark:border-border">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter breaks the line — the same rule as
                  // every other composer in the app.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                rows={1}
                maxLength={200}
                placeholder={t("controlBar.typeBroadcastMessage")}
                className="max-h-28 min-h-[42px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-primary dark:border-border dark:bg-muted dark:text-foreground dark:placeholder:text-muted-foreground"
              />
              <button
                onClick={handleSend}
                disabled={!canSend}
                aria-label={t("controlBar.broadcast")}
                className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-full bg-primary text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            {/* Under the composer, where the host is already looking. The
                audience is sticky while this screen is open, so the one thing
                they must not have to remember is who they narrowed it to three
                messages ago — and a narrowed broadcast does NOT reach anyone
                still sitting in the main room, which is invisible from here. */}
            <div className="mt-2 flex items-center justify-between gap-2 px-1">
              <p className="min-w-0 truncate text-[11px]">
                {allSelected ? (
                  <span className="text-slate-500 dark:text-muted-foreground">
                    Going to the main room and every breakout
                  </span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-400">
                    Going to{" "}
                    <span className="font-semibold">
                      {targets.map((a) => a.name).join(", ") || "nowhere"}
                    </span>{" "}
                    only — nobody else will see it
                  </span>
                )}
              </p>
              <span className="shrink-0 text-[11px] text-slate-400 dark:text-muted-foreground">
                {message.length}/200
              </span>
            </div>
          </div>

          {/* Row 4 — rooms on the left, the way out on the right */}
          <div className="flex items-center gap-3 border-t border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-border dark:bg-muted/20">
            <div className="min-w-0 flex-1">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-muted-foreground">
                {t("controlBar.recipients")}
              </p>
              <div className="flex max-h-16 flex-wrap gap-1.5 overflow-y-auto">
                <RoomChip
                  label={t("controlBar.allRooms")}
                  selected={allSelected}
                  onClick={selectAllRooms}
                />
                {audience.map((a) => (
                  <RoomChip
                    key={a.id}
                    label={a.count === null ? a.name : `${a.name} (${a.count})`}
                    selected={!allSelected && selectedRoomIds.includes(a.id)}
                    onClick={() => toggleRoom(a.id)}
                  />
                ))}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 self-end">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                {t("controlBar.cancel")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** One room in the footer picker. A chip rather than a checkbox row: the whole
 *  thing is the target, which is what you want for something clicked mid-call. */
function RoomChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        selected
          ? "border-primary bg-primary text-white"
          : "border-slate-200 bg-card text-slate-600 hover:border-primary/40 hover:text-primary dark:border-border dark:bg-muted dark:text-muted-foreground"
      }`}
    >
      {selected && <Check className="h-3 w-3" />}
      <span className="truncate">{label}</span>
    </button>
  )
}
